import { createModel } from '@rematch/core';
import produce from 'immer';
import nonCriticalException from '../modules/exceptionLogger';
import {
  addProviderProduct,
  deleteProviderProduct,
  editProviderProduct,
  getProviderProducts,
} from '../api/Providers/Products/Products';
import type { RootModel } from './models';

type State = {
  loading: boolean;
  providerId: number | null;
  list: any[];
  errors: any[] | null;
};

const defaultState = (): State => ({
  loading: false,
  providerId: null,
  list: [],
  errors: null,
});

/**
 * Manages the list of provider products for a given provider
 */
const ProviderProductsV2 = createModel<RootModel>()({
  state: defaultState(),

  reducers: {
    /**
     * Called when we've begun fetching products for a provider
     */
    onFetchStart: (_, payload: { providerId: number }) => ({
      ...defaultState(),
      providerId: payload.providerId,
      loading: true,
      errors: null,
    }),

    /**
     * Called when we've begun fetching products for a provider
     */
    onRefetchStart: produce(state => {
      state.errors = null;
      state.loading = true;
    }),

    /**
     * Called when we are finished fetching products for a provider
     */
    onFetchComplete: produce<State, [{ products: any[] }]>((state, payload) => {
      state.list = [...payload.products];
      state.loading = false;
    }),

    onDeleteStart: produce(state => {
      state.loading = true;
    }),

    onError: produce<State, [{ error: any }]>((state, { error }) => {
      state.loading = false;

      state.errors = [error];
    }),

    updateItem: produce<State, [{ product: any }]>((state, payload) => {
      state.loading = false;
      state.errors = null;

      const idx = state.list.findIndex(item => item.id === payload.product.id);
      state.list[idx] = payload.product;
    }),

    addItem: produce<State, [{ product: any }]>((state, payload) => {
      state.loading = false;
      state.errors = null;
      state.list.push(payload.product);
    }),

    clear: () => defaultState(),
  },

  effects: dispatch => ({
    /**
     * Loads a list of products for the given provider
     * @param providerId
     */
    load: async ({ providerId }: { providerId: number }, rootState): Promise<any> => {
      if (providerId !== rootState.providerProductsV2.providerId) {
        dispatch.providerProductsV2.onFetchStart({ providerId });
      } else {
        dispatch.providerProductsV2.onRefetchStart();
      }

      try {
        const products = await getProviderProducts(providerId);
        return dispatch.providerProductsV2.onFetchComplete({ products });
      } catch (error) {
        nonCriticalException(error);
        return dispatch.providerProductsV2.onError({ error });
      }
    },

    delete: async ({
      providerId,
      productId,
    }: {
      providerId: number;
      productId: number;
    }): Promise<any> => {
      dispatch.providerProductsV2.onDeleteStart();
      try {
        await deleteProviderProduct(providerId, productId);
      } catch (error) {
        nonCriticalException(error);
        dispatch.providerProductsV2.onError({ error });
        return null;
      }

      return dispatch.providerProductsV2.load({ providerId });
    },

    /**
     * Edit or create a product
     */
    save: async ({ providerId, product }: { providerId: number; product: any }): Promise<any> => {
      dispatch.providerProductsV2.onRefetchStart();

      let result;

      try {
        if (product.id) {
          result = await editProviderProduct(providerId, product);
          dispatch.providerProductsV2.updateItem({ product: result });
        } else {
          result = await addProviderProduct(providerId, { ...product, has_tax: false });
          dispatch.providerProductsV2.addItem({ product: result });
        }
      } catch (error) {
        dispatch.providerProductsV2.onError({ error });
        return null;
      }

      return result;
    },
  }),
});

export default ProviderProductsV2;
